import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { Beaker, ArrowLeft, RefreshCw, Lightbulb, Code, User, Loader2 } from 'lucide-react';
import { Card, Button, Badge } from '../../../components/ui';
import { StrategyBadge } from '../../../components/jobs';
import { useToastActions } from '../../../components/ui';
import { ResumeSwitcher } from '../../../components/ResumeSwitcher';
import { useResumeContext } from '../../../hooks/useResumeContext';
import { apiFetch } from '../../../lib/api-client';
import type { ResumeTag, QuerySpec } from '../../../types';

interface StrategyLabData {
  tags: ResumeTag[];
  queries: QuerySpec[];
  sources?: {
    resume_tags: number;
    profile_data: boolean;
    objectives: boolean;
  };
}

export function StrategyLabPage() {
  const { selectedResumeId, selectedResume, loading: resumeLoading } = useResumeContext();
  const navigate = useNavigate();
  const { success, error } = useToastActions();
  
  // State for strategy lab data
  const [tags, setTags] = useState<ResumeTag[]>([]);
  const [queries, setQueries] = useState<QuerySpec[]>([]);
  const [sources, setSources] = useState<StrategyLabData['sources']>();
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  
  const [selectedStrategy, setSelectedStrategy] = useState<string>('all');
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Load strategy lab data when resume changes
  useEffect(() => {
    if (selectedResumeId && !resumeLoading) {
      loadStrategyLabData();
    }
  }, [selectedResumeId, resumeLoading]);

  const loadStrategyLabData = async () => {
    if (!selectedResumeId) return;
    
    setLoading(true);
    setLoadError(null);
    
    try {
      // Load resume tags and generate dry run queries
      const [tagsResponse, queriesResponse] = await Promise.allSettled([
        apiFetch(`/api/v2/tags/from-resume?resumeId=${selectedResumeId}`),
        apiFetch(`/api/v2/strategy/build-queries?resumeId=${selectedResumeId}&dry_run=true&count=10`)
      ]);

      // Process tags response
      let tagsData: ResumeTag[] = [];
      if (tagsResponse.status === 'fulfilled' && tagsResponse.value.ok) {
        const data = await tagsResponse.value.json();
        if (data.success) {
          tagsData = data.data || [];
        }
      }

      // Process queries response
      let queriesData: QuerySpec[] = [];
      let sourcesData: StrategyLabData['sources'] = { resume_tags: 0, profile_data: false, objectives: false };
      if (queriesResponse.status === 'fulfilled' && queriesResponse.value.ok) {
        const data = await queriesResponse.value.json();
        if (data.success) {
          queriesData = data.data?.queries || [];
          sourcesData = data.data?.sources || sourcesData;
        }
      }

      setTags(tagsData);
      setQueries(queriesData);
      setSources(sourcesData);
    } catch (e: any) {
      console.error('Failed to load strategy lab data:', e);
      setLoadError(e?.message || 'Failed to load strategy lab data');
    } finally {
      setLoading(false);
    }
  };

  const handleDryRun = async () => {
    if (!selectedResumeId) return;
    
    setIsRefreshing(true);
    try {
      const response = await apiFetch(`/api/v2/strategy/build-queries?resumeId=${selectedResumeId}&dry_run=true&count=10`);
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setQueries(data.data?.queries || []);
          setSources(data.data?.sources);
          success('Queries Refreshed', `Generated ${data.data?.queries?.length || 0} new query variations.`);
        } else {
          throw new Error(data.error?.message || 'Failed to generate queries');
        }
      } else {
        throw new Error(`Failed to generate queries: ${response.status}`);
      }
    } catch (e: any) {
      console.error('Failed to refresh queries:', e);
      error('Refresh Failed', e?.message || 'Unable to generate new queries. Please try again.');
    } finally {
      setIsRefreshing(false);
    }
  };

  // Group tags by kind
  const tagsByKind = tags.reduce((acc, tag) => {
    if (!acc[tag.kind]) acc[tag.kind] = [];
    acc[tag.kind].push(tag);
    return acc;
  }, {} as Record<string, ResumeTag[]>);

  // Filter queries by selected strategy
  const filteredQueries = selectedStrategy === 'all' 
    ? queries 
    : queries.filter(q => q.strategy_key === selectedStrategy);

  // Get unique strategies for filtering
  const uniqueStrategies = Array.from(new Set(queries.map(q => q.strategy_key)));

  const tagKindLabels = {
    skill: 'Technical Skills',
    role: 'Roles & Titles', 
    domain: 'Domain Knowledge',
    experience: 'Experience Level',
    certification: 'Certifications',
  };

  const tagKindIcons = {
    skill: Code,
    role: User,
    domain: Lightbulb,
    experience: Beaker,
    certification: Badge,
  };

  // Show loading state while resume context loads
  if (resumeLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center py-12">
          <div className="flex items-center space-x-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-muted-foreground">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  // Show message when no resume is selected
  if (!selectedResumeId) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Button variant="ghost" size="sm" onClick={() => navigate('/strategy')}>
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <Beaker className="h-6 w-6 text-primary" />
              <h1 className="text-3xl font-bold text-foreground">Strategy Lab</h1>
            </div>
            <p className="text-muted-foreground ml-11">
              Select a resume to explore how it generates targeted search queries.
            </p>
          </div>
          <ResumeSwitcher className="w-64" />
        </div>
        
        <Card>
          <div className="text-center py-12 bg-champagne-pink/10 rounded-2xl">
            <Beaker className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-foreground mb-2">No Resume Selected</h3>
            <p className="text-muted-foreground mb-4">
              Upload a resume to see how AI generates personalized search queries.
            </p>
            <Button onClick={() => navigate('/resumes')} className="gap-2">
              Upload Resume
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <Button variant="ghost" size="sm" onClick={() => navigate('/strategy')}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <Beaker className="h-6 w-6 text-primary" />
            <h1 className="text-3xl font-bold text-foreground">Strategy Lab</h1>
          </div>
          <p className="text-muted-foreground ml-11">
            {selectedResume ? `Explore queries for ${selectedResume.filename}` : 'Explore how your resume and preferences generate targeted search queries.'}
          </p>
        </div>
        <ResumeSwitcher className="w-64" />
      </div>

      {/* Error display */}
      {loadError && (
        <Card>
          <div className="text-center py-8 bg-destructive/10 rounded-2xl">
            <div className="text-destructive mb-2">Error loading strategy lab data</div>
            <p className="text-muted-foreground mb-4">{loadError}</p>
            <Button onClick={loadStrategyLabData} variant="outline">
              Try Again
            </Button>
          </div>
        </Card>
      )}

      {/* Loading state */}
      {loading && (
        <Card>
          <div className="text-center py-8">
            <div className="flex items-center justify-center space-x-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-muted-foreground">Loading strategy lab data...</span>
            </div>
          </div>
        </Card>
      )}

      {/* Sources Summary */}
      {sources && !loading && (
        <Card>
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-foreground">Data Sources</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex items-center gap-3">
                <div className={`h-3 w-3 rounded-full ${sources.resume_tags > 0 ? 'bg-green-500' : 'bg-gray-500'}`} />
                <div>
                  <span className="font-medium">Resume Tags</span>
                  <p className="text-sm text-muted-foreground">{sources.resume_tags} extracted tags</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <div className={`h-3 w-3 rounded-full ${sources.profile_data ? 'bg-green-500' : 'bg-gray-500'}`} />
                <div>
                  <span className="font-medium">Profile Data</span>
                  <p className="text-sm text-muted-foreground">
                    {sources.profile_data ? 'Available' : 'Not configured'}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <div className={`h-3 w-3 rounded-full ${sources.objectives ? 'bg-green-500' : 'bg-gray-500'}`} />
                <div>
                  <span className="font-medium">Objectives</span>
                  <p className="text-sm text-muted-foreground">
                    {sources.objectives ? 'Configured' : 'Default'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Card>
      )}

      {!loading && !loadError && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Resume Tags */}
        <Card>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-foreground">Resume Analysis</h2>
              <Badge variant="secondary">{tags.length} tags</Badge>
            </div>

            {tags.length > 0 ? (
              <div className="space-y-4">
                {Object.entries(tagsByKind).map(([kind, kindTags]) => {
                  const IconComponent = tagKindIcons[kind as keyof typeof tagKindIcons] || Code;
                  
                  return (
                    <div key={kind}>
                      <div className="flex items-center gap-2 mb-2">
                        <IconComponent className="h-4 w-4 text-muted-foreground" />
                        <h3 className="font-medium text-foreground text-sm">
                          {tagKindLabels[kind as keyof typeof tagKindLabels] || kind}
                        </h3>
                        <Badge variant="secondary">{kindTags.length}</Badge>
                      </div>
                      
                      <div className="flex flex-wrap gap-1">
                        {kindTags
                          .sort((a, b) => b.confidence - a.confidence)
                          .slice(0, 8)
                          .map((tag, index) => (
                            <Badge 
                              key={index}
                              variant="secondary" 
                              className="text-xs"
                              title={`${tag.tag} (${Math.round(tag.confidence * 100)}% confidence)`}
                            >
                              {tag.tag}
                            </Badge>
                          ))}
                        {kindTags.length > 8 && (
                          <Badge variant="secondary" className="text-xs">
                            +{kindTags.length - 8} more
                          </Badge>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-6">
                <Code className="h-12 w-12 text-muted-foreground/50 mx-auto mb-3" />
                <p className="text-muted-foreground text-sm">
                  No resume tags available. Upload and process your resume to see AI-generated tags.
                </p>
              </div>
            )}
          </div>
        </Card>

        {/* Generated Queries */}
        <div className="lg:col-span-2 space-y-4">
          <Card>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-foreground">Generated Search Queries</h2>
                <div className="flex items-center gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleDryRun}
                    disabled={isRefreshing}
                    className="gap-1"
                  >
                    {isRefreshing ? (
                      <RefreshCw className="h-4 w-4 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4" />
                    )}
                    {isRefreshing ? 'Refreshing...' : 'Dry Run'}
                  </Button>
                </div>
              </div>

              {/* Strategy Filter */}
              {uniqueStrategies.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant={selectedStrategy === 'all' ? 'secondary' : 'ghost'}
                    size="sm"
                    onClick={() => setSelectedStrategy('all')}
                  >
                    All ({queries.length})
                  </Button>
                  {uniqueStrategies.map((strategy) => (
                    <Button
                      key={strategy}
                      variant={selectedStrategy === strategy ? 'secondary' : 'ghost'}
                      size="sm"
                      onClick={() => setSelectedStrategy(strategy)}
                    >
                      {strategy.replace(/_STRATEGY$/, '').replace(/_/g, ' ')} 
                      ({queries.filter(q => q.strategy_key === strategy).length})
                    </Button>
                  ))}
                </div>
              )}

              {filteredQueries.length > 0 ? (
                <div className="space-y-3">
                  {filteredQueries.map((query, index) => (
                    <div key={index} className="bg-muted/30 rounded-2xl p-4">
                      <div className="flex items-start gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <code className="bg-background/50 px-2 py-1 rounded text-sm font-mono">
                              {query.query}
                            </code>
                            <StrategyBadge 
                              attribution={{ 
                                strategy_key: query.strategy_key,
                                confidence_score: query.estimated_hit_rate 
                              }} 
                              size="sm"
                            />
                            {query.estimated_hit_rate && (
                              <Badge variant="secondary" className="text-xs">
                                ~{Math.round(query.estimated_hit_rate * 100)} results
                              </Badge>
                            )}
                          </div>
                          
                          <p className="text-sm text-muted-foreground mb-2">
                            {query.rationale}
                          </p>

                          {/* Query Filters */}
                          {Object.keys(query.filters).length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {Object.entries(query.filters).map(([key, value]) => {
                                if (!value) return null;
                                return (
                                  <Badge key={key} variant="secondary" className="text-xs">
                                    {key}: {String(value)}
                                  </Badge>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Lightbulb className="h-12 w-12 text-muted-foreground/50 mx-auto mb-3" />
                  <p className="text-muted-foreground">
                    {selectedStrategy === 'all' 
                      ? 'No queries generated. Complete your profile and resume to see personalized search queries.'
                      : `No queries found for the ${selectedStrategy} strategy.`}
                  </p>
                </div>
              )}
            </div>
          </Card>

          {/* Publish Action */}
          <Card>
            <div className="space-y-4">
              <div>
                <h2 className="text-lg font-semibold text-foreground">Publish Strategy</h2>
                <p className="text-sm text-muted-foreground">
                  Deploy these queries to your job recommendation system.
                </p>
              </div>

              <div className="flex items-center justify-between">
                <div className="text-sm text-muted-foreground">
                  Ready to publish {filteredQueries.length} optimized queries
                </div>
                <Button disabled className="gap-2">
                  <Lightbulb className="h-4 w-4" />
                  Publish (Coming Soon)
                </Button>
              </div>

              <div className="text-sm text-muted-foreground bg-muted/30 rounded-xl p-3">
                <strong>Note:</strong> Strategy deployment is automatic. This lab shows you how queries are generated 
                from your profile data. Changes to your objectives and resume will automatically update your recommendations.
              </div>
            </div>
          </Card>
        </div>
      </div>
      )}
    </div>
  );
}